/**
 * SPAWN READINESS — may the player be placed here yet?
 *
 * The display pipeline draws COARSE terrain wherever lod-0 has not arrived, so a spot can look
 * perfectly solid on screen while the physics engine — which reads lod-0 voxels only — knows
 * nothing about it. Placing the player there does not put them on that coarse ground: the engine
 * refuses to simulate into unknown space (R61-LOD0-SUPPORT: standing means standing on real
 * voxels), so they freeze in mid-air over a surface they can see. That is the "hit Play and spawn
 * before lod-0 has loaded" bug, and it is a placement bug, not a physics one.
 *
 * The predicate is pure and probe-driven for the same reason CoverageCore is: the decision is
 * testable on its own, without a world, workers or a renderer.
 *
 * TWO conditions, because they answer different questions:
 *   - KNOWN: every chunk the capsule occupies (plus the support margin below the feet) is either
 *     resident lod-0 data or provable open sky. This is the same question `stepPlayer` asks before
 *     it agrees to simulate, so satisfying it is exactly "the player will not freeze on arrival".
 *   - MESHED: the chunk holding the ground under the feet has a finished mesh. Data alone would
 *     drop the player into a world they cannot see yet.
 */

import { PLAYER_RADIUS, PLAYER_HEIGHT_INNER, MAX_SNAP_DISTANCE, CHUNK_SIZE, VOXEL_SCALE } from '@worldify/shared';

/** Facts about lod-0 residency — implemented by VoxelWorld live, by fixtures in tests. */
export interface SpawnProbe {
  /** lod-0 chunk resident with voxel data (NOT coarse — coarse is not collidable). */
  isResident(cx: number, cy: number, cz: number): boolean;
  /** lod-0 chunk resident AND its mesh applied complete (it is drawable). */
  isMeshed(cx: number, cy: number, cz: number): boolean;
  /** Provable open sky: no data exists there and none ever will (above the column's content top). */
  isKnownAir(cx: number, cy: number, cz: number): boolean;
}

const CHUNK_WORLD = CHUNK_SIZE * VOXEL_SCALE;
/** Feet are this far below the eye — the capsule's own geometry, matching the physics hull. */
export const FEET_BELOW_EYE = PLAYER_HEIGHT_INNER + PLAYER_RADIUS;
/**
 * How far below the feet must also be known. The solver looks down by the snap distance plus the
 * capsule radius to find support, so a spawn whose support margin is unknown freezes on arrival
 * even though the capsule's own volume is known.
 */
const SUPPORT_MARGIN = PLAYER_RADIUS + MAX_SNAP_DISTANCE + 0.2;

const chunkOf = (v: number): number => Math.floor(v / CHUNK_WORLD);

/**
 * Is `(x, y, z)` (an EYE position) ready to receive the player?
 *
 * Scans the chunks overlapping the capsule's AABB widened by its radius and deepened by the
 * support margin. Every one must be resident or provable sky; the chunk under the feet must
 * additionally be meshed, unless the feet are in open sky (a legitimate spawn ABOVE the surface —
 * the player falls, which the engine handles, and the ground below still has to be real).
 */
export function isSpawnReadyAt(probe: SpawnProbe, x: number, y: number, z: number): boolean {
  const feetY = y - FEET_BELOW_EYE;
  const minX = chunkOf(x - PLAYER_RADIUS), maxX = chunkOf(x + PLAYER_RADIUS);
  const minZ = chunkOf(z - PLAYER_RADIUS), maxZ = chunkOf(z + PLAYER_RADIUS);
  const minY = chunkOf(feetY - SUPPORT_MARGIN), maxY = chunkOf(y);

  for (let cx = minX; cx <= maxX; cx++) {
    for (let cz = minZ; cz <= maxZ; cz++) {
      for (let cy = minY; cy <= maxY; cy++) {
        if (probe.isResident(cx, cy, cz)) continue;
        if (probe.isKnownAir(cx, cy, cz)) continue;
        return false; // unknown space inside the capsule's own volume → the engine would freeze
      }
    }
  }

  // The ground itself must be drawable, not merely present. Sky under the feet means this is a
  // fall-in spawn: the chunks below were checked above, so there is real terrain to land on.
  const supportCy = chunkOf(feetY - VOXEL_SCALE);
  const supportCx = chunkOf(x), supportCz = chunkOf(z);
  if (probe.isKnownAir(supportCx, supportCy, supportCz)) return true;
  return probe.isMeshed(supportCx, supportCy, supportCz);
}
